import React, { useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import InnerLayout from '../../../../../../layout/Inner';
import { Typography, Box, Chip, Grid, Paper } from '@mui/material';
import { Loader } from '@omnigenbiodata/ui';
import {
  resetStagedShipmentIsBusySelector,
  resetStagedShipmentResponseSelector,
} from '../../../../../../store/stagedShipmentReset/selectors';
import Buttons from '../../../../../../components/forms/Buttons';
import moment from 'moment';
import { DATE_FORMAT } from '../../../../../../core/constants/forms.constants';
import { CreateStagedShipmentInput } from '../../../../../../core/api/lab.types';
import {
  getStagedShipmentMetaResponseSelector,
  getStagedShipmentMetaThunk,
  resetStagedShipmentMeta,
} from '../../../../../../store/stagedShipmentMetaList';
import ROUTES from '../../../../../../core/constants/routes.constants';
import { resetStagedShipmentThunk } from '../../../../../../store/stagedShipmentReset/thunks';
import { createStagedShipmentReset } from '../../../../../../store/stagedShipmentCreate';
import { resetStagedShipment } from '../../../../../../store/stagedShipmentReset';

function ResetShipmentScene() {
  const { t } = useTranslation('portal');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const stagedMeta = useAppSelector(getStagedShipmentMetaResponseSelector);
  const resetStagedShipmentIsBusy = useAppSelector(resetStagedShipmentIsBusySelector);
  const resetStagedShipmentResponse = useAppSelector(resetStagedShipmentResponseSelector);
  const [shipmentData, setShipmentData] = useState<CreateStagedShipmentInput>({
    destination: '',
    shipmentType: '',
    plannedShipmentDate: '',
  });

  useEffect(() => {
    if (stagedMeta?.destinationLocationUuid) {
      setShipmentData({
        destination: stagedMeta.destinationLocation,
        shipmentType: stagedMeta.shipmentType,
        plannedShipmentDate: stagedMeta.plannedShipmentDate,
      });
    }
  }, [stagedMeta]);

  useEffect(() => {
    dispatch(getStagedShipmentMetaThunk());
  }, [dispatch]);

  const goBack = () => {
    navigate(ROUTES.shipping);
  };

  const handleResetShipmentConfirm = () => {
    dispatch(resetStagedShipmentThunk());
  };

  useEffect(() => {
    const handleResetAndNavigate = async () => {
      if (resetStagedShipmentResponse?.stagedShipmentMetaUuid) {
        dispatch(resetStagedShipmentMeta());
        dispatch(resetStagedShipment());
        dispatch(createStagedShipmentReset());

        navigate(ROUTES.root);
      }
    };

    handleResetAndNavigate();
  }, [dispatch, navigate, resetStagedShipmentResponse]);

  return (
    <InnerLayout>
      <Container maxWidth="md">
        <>
          <Typography mb={2} variant="h3" align="center">
            {t('resetShipment.title')}
          </Typography>

          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Typography variant="h5" fontWeight={600}>
                Shipment Summary
              </Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                  Shipment Status
                </Typography>

                <Chip
                  label="STAGED"
                  sx={{
                    bgcolor: '#1976d2',
                    color: '#fff',
                    fontWeight: 700,
                    minWidth: 100,
                    height: 30,
                    borderRadius: 20,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                  Shipment Destination
                </Typography>

                <Typography fontWeight={600}>{shipmentData.destination}</Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                  Shipment Type
                </Typography>

                <Typography fontWeight={600}>{shipmentData.shipmentType}</Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                  Shipment Date
                </Typography>

                <Typography fontWeight={600}>{moment(shipmentData.plannedShipmentDate).format(DATE_FORMAT)}</Typography>
              </Grid>
            </Grid>
          </Paper>
          <Box
            component="form"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              p: 10,
              mb: 4,
              mt: 2,
              boxShadow: 1,
            }}
          >
            <Typography mt={0} mb={4} variant="h5" align="center" fontWeight={600}>
              {t('resetShipment.text')}
            </Typography>

            <Buttons
              forwardLabel={t('buttons.confirm')}
              onForward={handleResetShipmentConfirm}
              onBack={goBack}
              backLabel={t('buttons.back')}
            />
          </Box>
          <Loader
            data-testid="loading-message"
            isVisible={resetStagedShipmentIsBusy}
            label={t('loaders.resetStagedShipment') as string}
          />
        </>
      </Container>
    </InnerLayout>
  );
}

export default ResetShipmentScene;
